import { useEffect, useMemo, useRef, useState } from 'react';
import {
  type CreatePromoCodePayload,
  type PromoCodeDetailsResponse,
  type PromoCodeDto,
  type PromoCodeType,
  promoCodesService,
} from '@/services/promo-codes.service';
import { usePromoCodesStore } from '@/store/promo-codes.store';
import { AdminPromoCodeModal, type PromoFormState } from './AdminPromoCodeModal';
import { AdminPromoCodeDetailsPanel } from '@/components/AdminPromoCodeDetailsPanel';
import { formatDateOnlyForDisplay, parseDateOnly } from '@/utils/date-only';
import { copyTextToClipboard } from '@/utils/clipboard';
import styles from './AdminPromoCodesSection.module.css';
import emptyPromoCodesIcon from '@/assets/icons/empty-promocodes.svg';
import skipassIcon from '@/assets/icons/skipass.svg';
import expandIcon from '@/assets/icons/expand.svg';
import copyGrayIcon from '@/assets/icons/copy_gray.svg';
import { useNavigate, useParams } from 'react-router-dom';

type PromoStatusFilter = 'all' | 'active' | 'inactive' | 'redeemed';

const PAGE_SIZE = 10;

const generateInitialPromoCode = (length = 8) => {
  const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  return Array.from({ length }, () => alphabet[Math.floor(Math.random() * alphabet.length)]).join('');
};

const formatDate = (value?: string | null) => {
  if (!value) {
    return '-';
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return '-';
  }

  return parsed.toLocaleString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
};

const formatValidity = (startDate?: string | null, endDate?: string | null) => {
  if (!startDate && !endDate) {
    return '-';
  }

  const from = startDate ? formatDateOnlyForDisplay(startDate) : null;
  const to = endDate ? formatDateOnlyForDisplay(endDate) : null;
  return from && to ? `${from} - ${to}` : from ? `from ${from}` : to ? `until ${to}` : '-';
};

const formatValue = (type: PromoCodeType, value: number, asCents?: boolean) => {
  if (type === 'percentage') {
    return `${value}%`;
  }

  const amount = !asCents ? value : (value / 100).toFixed(2);

  return `${amount} EUR`;
};

const formatAmount = (value?: number | null, asCents?: boolean) => {
  if (value == null) {
    return '-';
  }

  const amount = !asCents ? value : (value / 100).toFixed(2);
  return `${amount} EUR`;
};

const getStatusLabel = (promoCode: PromoCodeDto) => {
  if (promoCode.isRedeemed) {
    return 'Redeemed';
  }

  return promoCode.active ? 'Active' : 'Inactive';
};

const getStatusClassName = (promoCode: PromoCodeDto) => {
  if (promoCode.isRedeemed) {
    return styles.statusRedeemed;
  }

  return promoCode.active ? styles.statusActive : styles.statusInactive;
};

const getDefaultForm = (options?: { withGeneratedCode?: boolean }): PromoFormState => ({
  type: 'percentage',
  value: '',
  code: options?.withGeneratedCode ? generateInitialPromoCode() : '',
  amount: '10',
  oneTime: false,
  minOrderAmount: '',
  startDate: '',
  endDate: '',
  domains: [],
  active: true,
});

export const AdminPromoCodesSection = () => {
  const navigate = useNavigate();
  const { promoCodeId: promoCodeIdParam } = useParams<{ promoCodeId?: string }>();
  const promoCodes = usePromoCodesStore((state) => state.promoCodes);
  const total = usePromoCodesStore((state) => state.total);
  const isLoading = usePromoCodesStore((state) => state.isLoading);
  const error = usePromoCodesStore((state) => state.error);
  const fetchPromoCodes = usePromoCodesStore((state) => state.fetchPromoCodes);
  const createPromoCode = usePromoCodesStore((state) => state.createPromoCode);
  const createPromoCodesBulk = usePromoCodesStore((state) => state.createPromoCodesBulk);
  const setPromoCodeActive = usePromoCodesStore((state) => state.setPromoCodeActive);
  const setError = usePromoCodesStore((state) => state.setError);
  const [overlayLeft, setOverlayLeft] = useState(0);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<PromoStatusFilter>('all');
  const [type, setType] = useState<'all' | PromoCodeType>('all');
  const [dateRange, setDateRange] = useState<{ from?: string; to?: string }>({});
  const [domains, setDomains] = useState<string[]>([]);
  const [page, setPage] = useState(1);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [form, setForm] = useState<PromoFormState>(getDefaultForm);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [updatingStatusIds, setUpdatingStatusIds] = useState<Record<string, boolean>>({});
  const [selectedPromoDetails, setSelectedPromoDetails] = useState<PromoCodeDetailsResponse | null>(null);
  const [isPromoDetailsLoading, setIsPromoDetailsLoading] = useState(false);
  const [copyToast, setCopyToast] = useState<{ visible: boolean; x: number; y: number }>({
    visible: false,
    x: 0,
    y: 0,
  });
  const copyResetTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const selectedPromoCodeId = useMemo(() => {
    if (!promoCodeIdParam?.trim()) {
      return null;
    }

    return promoCodeIdParam;
  }, [promoCodeIdParam]);

  useEffect(() => {
    const updateOverlayLeft = () => {
      if (!wrapperRef.current) return;
      setOverlayLeft(wrapperRef.current.scrollLeft + wrapperRef.current.clientWidth / 2);
    };

    updateOverlayLeft();
    window.addEventListener('resize', updateOverlayLeft);
    return () => {
      window.removeEventListener('resize', updateOverlayLeft);
    };
  }, []);

  useEffect(() => {
    return () => {
      if (copyResetTimeoutRef.current) {
        clearTimeout(copyResetTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      void fetchPromoCodes({
        page,
        limit: PAGE_SIZE,
        search,
        status,
        type,
        dateFrom: dateRange.from,
        dateTo: dateRange.to,
        domains,
      });
    }, 200);

    return () => {
      window.clearTimeout(timer);
    };
  }, [fetchPromoCodes, page, search, status, type, dateRange.from, dateRange.to, domains]);

  useEffect(() => {
    if (!selectedPromoCodeId) {
      setSelectedPromoDetails(null);
      setIsPromoDetailsLoading(false);
      return;
    }

    let isCancelled = false;
    setIsPromoDetailsLoading(true);

    void promoCodesService
      .getDetails(selectedPromoCodeId)
      .then((details) => {
        if (isCancelled) {
          return;
        }

        setSelectedPromoDetails(details);
      })
      .catch(() => {
        if (isCancelled) {
          return;
        }

        setSelectedPromoDetails(null);
      })
      .finally(() => {
        if (isCancelled) {
          return;
        }

        setIsPromoDetailsLoading(false);
      });

    return () => {
      isCancelled = true;
    };
  }, [selectedPromoCodeId]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const resetForm = (options?: { withGeneratedCode?: boolean }) => {
    setForm(getDefaultForm(options));
    setSubmitError(null);
    setError(null);
  };

  const closeAllModals = () => {
    setShowCreateModal(false);
    setShowBulkModal(false);
    resetForm();
  };

  const handleInput = (patch: Partial<PromoFormState>) => {
    setForm((prev) => ({ ...prev, ...patch }));
  };

  const validateCommon = () => {
    const value = Number(form.value);

    if (!Number.isInteger(value) || value <= 0) {
      return 'Value must be a positive integer.';
    }

    if (form.type === 'percentage' && value > 100) {
      return 'Percentage value must be less than or equal to 100.';
    }

    if (form.startDate && form.endDate && form.endDate < form.startDate) {
      return 'End date must be greater than or equal to start date.';
    }

    const minAmount = form.minOrderAmount ? Number(form.minOrderAmount) : null;
    if (minAmount != null && (!Number.isInteger(minAmount) || minAmount < 0)) {
      return 'Minimum amount must be a non-negative integer.';
    }

    return null;
  };

  const toCommonPayload = () => {
    const selectedDomains = form.domains.map(domain => ({
      id: domain.key,
      name: domain.label,
    }));

    return {
      type: form.type,
      value: Number(form.value),
      oneTime: form.oneTime,
      minOrderAmount: form.minOrderAmount ? Number(form.minOrderAmount) : null,
      startDate: parseDateOnly(form.startDate) ? form.startDate : null,
      endDate: parseDateOnly(form.endDate) ? form.endDate : null,
      domains: selectedDomains,
      active: form.active,
    };
  };

  const refreshCurrentPage = async () => {
    await fetchPromoCodes({
      page,
      limit: PAGE_SIZE,
      search,
      status,
      type,
      dateFrom: dateRange.from,
      dateTo: dateRange.to,
      domains,
    });
  };

  const onCreateSingle = async () => {
    const validationError = validateCommon();
    if (validationError) {
      setSubmitError(validationError);
      return;
    }

    const code = form.code.trim().toUpperCase();
    if (!code) {
      setSubmitError('Code is required.');
      return;
    }

    setSubmitError(null);

    try {
      const payload: CreatePromoCodePayload = {
        ...toCommonPayload(),
        code,
      };

      await createPromoCode(payload);
      await refreshCurrentPage();
      closeAllModals();
    } catch (err: unknown) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to create promo code.');
    }
  };

  const onCreateBulk = async () => {
    const validationError = validateCommon();
    if (validationError) {
      setSubmitError(validationError);
      return;
    }

    const amount = Number(form.amount);
    if (!Number.isInteger(amount) || amount <= 0) {
      setSubmitError('Amount of codes must be a positive integer.');
      return;
    }

    setSubmitError(null);

    try {
      await createPromoCodesBulk({
        ...toCommonPayload(),
        amount,
      });
      setPage(1);
      await fetchPromoCodes({
        page: 1,
        limit: PAGE_SIZE,
        search,
        status,
        type,
        dateFrom: dateRange.from,
        dateTo: dateRange.to,
        domains,
      });
      closeAllModals();
    } catch (err: unknown) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to generate promo codes.');
    }
  };

  const onChangeStatus = async (promoCode: PromoCodeDto, nextStatus: 'active' | 'inactive') => {
    if (promoCode.isRedeemed) {
      return;
    }

    const nextActive = nextStatus === 'active';
    if (promoCode.active === nextActive) {
      return;
    }

    setUpdatingStatusIds((prev) => ({ ...prev, [promoCode.id]: true }));

    try {
      await setPromoCodeActive(promoCode.id, nextActive);
      await refreshCurrentPage();
    } catch {
      return;
    } finally {
      setUpdatingStatusIds((prev) => {
        const next = { ...prev };
        delete next[promoCode.id];
        return next;
      });
    }
  };

  const onChangeStatusById = async (promoCodeId: string, nextStatus: 'active' | 'inactive') => {
    const promoCode = promoCodes.find((item) => item.id === promoCodeId) ?? selectedPromoDetails?.promoCode;

    if (!promoCode || promoCode.isRedeemed) {
      return;
    }

    const nextActive = nextStatus === 'active';
    if (promoCode.active === nextActive) {
      return;
    }

    setUpdatingStatusIds((prev) => ({ ...prev, [promoCodeId]: true }));

    try {
      await setPromoCodeActive(promoCodeId, nextActive);
      setSelectedPromoDetails((prev) => {
        if (!prev || prev.promoCode.id !== promoCodeId) {
          return prev;
        }

        return {
          ...prev,
          promoCode: {
            ...prev.promoCode,
            active: nextActive,
          },
        };
      });
      await refreshCurrentPage();
    } catch {
      return;
    } finally {
      setUpdatingStatusIds((prev) => {
        const next = { ...prev };
        delete next[promoCodeId];
        return next;
      });
    }
  };

  const openCreateSingle = () => {
    resetForm({ withGeneratedCode: true });
    setShowCreateModal(true);
    setShowBulkModal(false);
  };

  const switchToCreateSingle = () => {
    setShowCreateModal(true);
    setShowBulkModal(false);
  };

  const switchToCreateBulk = () => {
    setShowCreateModal(false);
    setShowBulkModal(true);
  };

  const resetFilters = () => {
    setStatus('all');
    setType('all');
    setDateRange({});
    setDomains([]);
    setSearch('');
    setPage(1);
  };

  const hasActiveFilters = useMemo(() => {
    return Boolean(
      Boolean(search.trim()) ||
      status != 'all' ||
      type != 'all'
    );
  }, [status, type, search]);

  const handleOpenPromoPanel = (promoCode: PromoCodeDto) => {
    void navigate(`/admin/promocodes/${promoCode.id}`);
  };

  const handleClosePromoPanel = () => {
    void navigate('/admin/promocodes');
  };

  const handleOpenOrderFromPromoPanel = (orderId: number) => {
    void navigate(`/admin/orders/${String(orderId)}`);
  };

  const handlePromoCodeCopy = async (code: string, position?: { x: number; y: number }) => {
    const copied = await copyTextToClipboard(code);
    if (!copied) {
      return;
    }

    const fallbackPosition = {
      x: window.innerWidth / 2,
      y: window.innerHeight / 2,
    };

    setCopyToast({
      visible: true,
      x: position?.x ?? fallbackPosition.x,
      y: position?.y ?? fallbackPosition.y,
    });

    if (copyResetTimeoutRef.current) {
      clearTimeout(copyResetTimeoutRef.current);
    }
    copyResetTimeoutRef.current = setTimeout(() => {
      setCopyToast((prev) => ({ ...prev, visible: false }));
      copyResetTimeoutRef.current = null;
    }, 1500);
  };

  return (
    <section className={styles.section}>
      {error ? <div className={styles.error}>{error}</div> : null}

      <div className={styles.controlsTop}>
        <div className={styles.searchBox}>
          <span className={styles.searchIcon}>⌕</span>
          <input
            type="text"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(1);
            }}
            placeholder="Search by code..."
            className={styles.searchInput}
          />
        </div>

        <div className={styles.actionsRow}>
          <button type="button" className={styles.primaryButton} onClick={openCreateSingle}>
            + Create new promo
          </button>
        </div>
      </div>

      <div className={styles.tableWrap} ref={wrapperRef}
          onScroll={() => {
            if (!wrapperRef.current) return;
            setOverlayLeft(wrapperRef.current.scrollLeft + wrapperRef.current.clientWidth / 2);
          }}>

        {isLoading ? <span className={styles.loading}>Loading…</span> : null}

        <table className={styles.table}>
          <thead>
            <tr>
              <th className={styles.colExpand}></th>
              <th>ID</th>
              <th>Code</th>
              <th>Value</th>
              <th>Usage type</th>
              <th>Time used</th>
              <th>Status</th>
              <th>Validity</th>
              <th>Min. order</th>
              <th>Domains</th>
              <th>Created by</th>
              <th>Created at</th>
              <th className={`${styles.stickyLast} ${styles.stickyHead}`}></th>
            </tr>
          </thead>
          <tbody>
            {promoCodes.map((promoCode) => (
              <tr key={promoCode.id}>
                <td className={styles.tdIcon}></td>
                <td>{promoCode.promoCodeId ?? '-'}</td>
                <td className={styles.codeCell}>
                  {promoCode.code ? (
                    <button
                      type="button"
                      className={styles.referenceButton}
                      onClick={(event) =>
                        void handlePromoCodeCopy(promoCode.code, {
                          x: event.clientX,
                          y: event.clientY,
                        })
                      }
                      title="Copy promo code"
                      aria-label={`Copy promo code ${promoCode.code}`}
                    >
                      <span className={styles.referenceValue}>{promoCode.code}</span>
                      <img src={copyGrayIcon} alt="" aria-hidden className={styles.referenceCopyIcon} />
                    </button>
                  ) : (
                    '-'
                  )}
                </td>
                <td>{formatValue(promoCode.type, promoCode.value, false)}</td>
                <td>{promoCode.oneTime ? 'Single-use' : 'Multi-use'}</td>
                <td>
                  <span className={styles.passesPill}>
                    {promoCode.timesUsed ?? 0}
                    <img src={skipassIcon} alt="Skipass" className={styles.skipassIcon} />
                  </span>
                </td>
                <td>
                  {promoCode.isRedeemed ? (
                    <span className={`${styles.statusPill} ${getStatusClassName(promoCode)}`}>
                      {getStatusLabel(promoCode)}
                    </span>
                  ) : (
                    <label
                      className={`${styles.statusToggle} ${
                        promoCode.active ? styles.statusToggleActive : styles.statusToggleInactive
                      }`}
                    >
                      <select
                        className={styles.statusSelect}
                        value={promoCode.active ? 'active' : 'inactive'}
                        disabled={Boolean(updatingStatusIds[promoCode.id])}
                        onChange={(event) => {
                          void onChangeStatus(
                            promoCode,
                            event.target.value === 'active' ? 'active' : 'inactive'
                          );
                        }}
                      >
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                      </select>
                      <span className={styles.statusChevron}>⌄</span>
                    </label>
                  )}
                </td>
                <td>{formatValidity(promoCode.startDate, promoCode.endDate)}</td>
                <td>{formatAmount(promoCode.minOrderAmount, false)}</td>
                <td>{promoCode.domains.length ? promoCode.domains.map((domain) => domain.name).join(', ') : '-'}</td>
                <td>{promoCode.createdByAdmin}</td>
                <td>{formatDate(promoCode.updatedAt)}</td>
                <td
                  className={`${styles.tdLast} ${styles.stickyLast}`}
                  onClick={() => handleOpenPromoPanel(promoCode)}
                >
                  <button
                    type="button"
                    className={styles.openOrderButton}
                    aria-label={`Promo code full details for code ${promoCode.code}`}
                  >
                    <img src={expandIcon} alt="" aria-hidden className={styles.openIcon} />
                  </button>
                </td>
              </tr>
            ))}

          </tbody>
        </table>
        {promoCodes.length === 0 && !isLoading && page === 1 ? (
          <div className={styles.noOrdersOverlay} style={{ left: overlayLeft }}>
            <div className={styles.noOrders}>
              <img src={emptyPromoCodesIcon} alt="No promo codes" className={styles.noOrdersIcon} />
              <span className={styles.noOrdersTitle}>No promo codes yet.</span><br />
              {hasActiveFilters ? (
                <>
                  <span>Filters may be too restrictive</span><br />
                  <button type="button" className={styles.noOrdersAction} onClick={resetFilters}>
                    Clear filters
                  </button>
                </>
              ) : null}
            </div>
          </div>
        ) : null}
        {copyToast.visible ? (
          <div
            className={styles.copyToast}
            style={{ left: copyToast.x, top: copyToast.y }}
            role="status"
            aria-live="polite"
          >
            Copied
          </div>
        ) : null}
      </div>

      {promoCodes.length === PAGE_SIZE || page > 1 ? (
        <div className={styles.pagination}>
          <span>
            {total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1} - {Math.min(page * PAGE_SIZE, total)} of {total}
          </span>
          <div className={styles.paginationButtons}>
            <button
              type="button"
              onClick={() => setPage((prev) => Math.max(1, prev - 1))}
              disabled={page <= 1}
              className={styles.secondaryButton}
            >
              Prev
            </button>
            <span>Page {page}</span>
            <button
              type="button"
              onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
              disabled={page >= totalPages}
              className={styles.secondaryButton}
            >
              Next
            </button>
          </div>
        </div>
      ) : null}

      <AdminPromoCodeModal
        showCreateModal={showCreateModal}
        showBulkModal={showBulkModal}
        form={form}
        submitError={submitError}
        onClose={closeAllModals}
        onOpenCreateSingle={switchToCreateSingle}
        onOpenCreateBulk={switchToCreateBulk}
        onInputChange={handleInput}
        onCreateSingle={onCreateSingle}
        onCreateBulk={onCreateBulk}
      />

      {selectedPromoDetails && !isPromoDetailsLoading ? (
        <AdminPromoCodeDetailsPanel
          details={selectedPromoDetails}
          isStatusUpdating={Boolean(updatingStatusIds[selectedPromoDetails.promoCode.id])}
          onClose={handleClosePromoPanel}
          onOpenOrder={handleOpenOrderFromPromoPanel}
          onChangeStatus={onChangeStatusById}
        />
      ) : null}
    </section>
  );
};
